// src/components/MAP/MAP.js
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MAP.css";
import VideoPlayer from "../VIDEO/VideoPlayer";
import VideoStory from "../Tabs/VideoStory";
import FilterControls from './components/FilterControls';
import { useIconFilters } from './hooks/useIconFilters';
import { useMapInitialization } from './hooks/useMapInitialization';
import { useTimeSync } from './hooks/useTimeSync';
import { useTrajectoryHighlight } from './hooks/useTrajectoryHighlight';
import { calculateBoundsWithBuffer } from "./utils/mapUtils";
import { useSharedState } from './hooks/useSharedState';

import { throttle, debounce, rafThrottle, isTabVisible } from "./utils/performance";
import videoSyncController from './controllers/VideoSyncController';
import tabSyncCoordinator from './coordinators/TabSyncCoordinator';

const MAP = forwardRef(({ csvFiles, onTimeUpdate, onTrackObject }, ref) => {
  // 使用共享状态hook
  const sharedState = useSharedState();
  
  // 使用共享钩子初始化地图
  const {
    mapInstance,
    routes,
    setRoutes,
    timelineData,
    polylinesRef,
    backgroundPolylinesRef,
    markersRef,
    customMarkersRef,
    routeIconsManager,
    setCurrentVisibleIcons,
    clearMap,
    rebuildMap,
    updateRobotPositions
  } = useMapInitialization('map', csvFiles, sharedState.timeframe);
  
  // 时间同步钩子
  const { handleSliderChange: syncTimeChange } = useTimeSync(
    timelineData, 
    polylinesRef, 
    markersRef, 
    mapInstance, 
    routes
  );
  
  // 图标过滤钩子
  const { visibleTypes, toggleIconType } = useIconFilters(customMarkersRef);
  
  // 本地UI状态
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const [isVideoVisible, setIsVideoVisible] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  // 创建refs
  const mapRef = useRef(null);
  const videoRef = useRef(null);
  const isComponentMounted = useRef(true);
  const robotPositionUpdatersRef = useRef({});
  
  // 用于跟踪地图交互和性能状态
  const isMapInteractionInProgress = useRef(false);
  const lastRobotUpdateTime = useRef(0);
  const UPDATE_THROTTLE_MS = 250; // 增加节流间隔以减少更新频率
  
  // 使用 rafThrottle 创建位置更新器
  const throttledPositionUpdater = useRef(null);
  
  // 使用轨迹高亮钩子
  const {
    highlightedTrajectory,
    highlightTrajectory,
    clearHighlight
  } = useTrajectoryHighlight({
    mapInstance,
    backgroundPolylinesRef,
    markersRef,
    robotPositionUpdatersRef,
    activeRobotId: sharedState.activeRobotId,
    timeframe: sharedState.timeframe,
    onRobotSelect: (robotId, robotVideoUrl) => {
      // 更新全局状态
      sharedState.updateVideoState({ 
        robotId: robotId,
        videoUrl: robotVideoUrl
      }, 'map');
    }
  });

  // 初始化 rafThrottle 更新器
  useEffect(() => {
    throttledPositionUpdater.current = rafThrottle((time) => {
      if (!isMapInteractionInProgress.current && updateRobotPositions) {
        updateRobotPositions(time, true);
      }
    });
    
    return () => {
      if (throttledPositionUpdater.current) {
        throttledPositionUpdater.current.cancel();
      }
    };
  }, [updateRobotPositions]);

  // 处理组件挂载/卸载
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
      
      // 清理任何挂起的防抖和节流
      if (throttledPositionUpdater.current) {
        throttledPositionUpdater.current.cancel();
      }
      if (debouncedUpdateRobotPositions) {
        debouncedUpdateRobotPositions.cancel();
      }
    };
  }, []);
  
  // 监听地图交互事件
  useEffect(() => {
    if (!mapInstance.current) return;
    
    const handleMapInteractionStart = () => {
      isMapInteractionInProgress.current = true;
      // 暂停视频播放时的地图更新
      if (isVideoPlaying) {
        if (throttledPositionUpdater.current) {
          throttledPositionUpdater.current.cancel();
        }
        if (debouncedUpdateRobotPositions) {
          debouncedUpdateRobotPositions.cancel();
        }
      }
    };
    
    const handleMapInteractionEnd = () => {
      // 延迟重置以确保所有事件都已处理完毕
      setTimeout(() => {
        isMapInteractionInProgress.current = false;
        
        // 地图交互结束后，如果不在播放状态，进行一次位置更新
        if (!isVideoPlaying && updateRobotPositions) {
          updateRobotPositions(sharedState.currentTime, false);
        }
      }, 300);
    };
    
    // 为地图添加交互事件监听
    mapInstance.current.on('zoomstart dragstart', handleMapInteractionStart);
    mapInstance.current.on('zoomend dragend', handleMapInteractionEnd);
    mapInstance.current.on('movestart', handleMapInteractionStart);
    mapInstance.current.on('moveend', handleMapInteractionEnd);
    
    return () => {
      if (mapInstance.current) {
        mapInstance.current.off('zoomstart dragstart', handleMapInteractionStart);
        mapInstance.current.off('zoomend dragend', handleMapInteractionEnd);
        mapInstance.current.off('movestart', handleMapInteractionStart);
        mapInstance.current.off('moveend', handleMapInteractionEnd);
      }
    };
  }, [mapInstance, isVideoPlaying, updateRobotPositions, sharedState.currentTime]);
  
  // 处理对象跟踪
  const handleTrackObject = useCallback((trackData) => {
    if (!isComponentMounted.current || !onTrackObject) return;
    onTrackObject(trackData);
  }, [onTrackObject]);

  // 优化：自定义防抖函数用于更新机器人位置
  const debouncedUpdateRobotPositions = useCallback(
    debounce((time) => {
      if (!isMapInteractionInProgress.current && isTabVisible() && updateRobotPositions) {
        updateRobotPositions(time, true);
      }
    }, 150),
    [updateRobotPositions]
  );

  // 优化：使用节流处理视频时间更新
  const handleVideoTimeUpdate = useCallback(
    throttle((time) => {
      if (!isComponentMounted.current || sharedState.processingUpdate) return;
      
      // 标记视频正在播放
      setIsVideoPlaying(true);
      
      // 添加空值检查
      if (time === undefined || time === null) {
        console.warn('MAP: Received invalid time value, ignoring update');
        return;
      }
      
      // 避免微小变化触发更新
      if (Math.abs(time - sharedState.currentTime) < 0.1) return;
      
      // 更新全局状态
      sharedState.updateVideoState({ time }, 'map');
      
      // 传统回调
      if (onTimeUpdate) {
        onTimeUpdate(time);
      }
      
      // 视频播放中避免频繁更新地图
      const now = Date.now();
      if (now - lastRobotUpdateTime.current < UPDATE_THROTTLE_MS) {
        return;
      }
      lastRobotUpdateTime.current = now;
      
      // 如果正在进行地图交互，跳过更新
      if (isMapInteractionInProgress.current) return;
      
      // 只有在标签页可见且不在地图交互时才更新
      if (isTabVisible() && throttledPositionUpdater.current) {
        throttledPositionUpdater.current(time);
      }
    }, 200),
    [sharedState.currentTime, sharedState.processingUpdate, sharedState.updateVideoState, onTimeUpdate]
  );

  // 视频选择处理
  const handleVideoSelect = useCallback(async (videoUrl, startTime, keepPlaying = false) => {
    if (!isComponentMounted.current || sharedState.processingUpdate) return Promise.resolve();
    
    // 添加空值检查
    if (startTime === undefined || startTime === null) {
      startTime = 0;
      console.warn('MAP: Received invalid startTime, defaulting to 0');
    }
    
    if (!videoUrl) {
      console.warn('MAP: Received invalid videoUrl, ignoring video selection');
      return Promise.resolve();
    }
    
    // 构建robotId
    const robotIdMatch = videoUrl.match(/video(\d+)/);
    const robotId = robotIdMatch ? parseInt(robotIdMatch[1]) : null;
    
    // 更新全局状态
    sharedState.updateVideoState({
      robotId,
      videoUrl,
      time: startTime,
      isPlaying: keepPlaying
    }, 'map');
    
    // 如果有视频引用，控制视频
    if (videoRef.current) {
      try {
        return await videoRef.current.seekTo(startTime, keepPlaying);
      } catch (error) {
        console.error('Error seeking video:', error);
      }
    }
    
    return Promise.resolve();
  }, [sharedState.processingUpdate, sharedState.updateVideoState]);
  
  // 事件块点击处理
  const handleTimeBlockClick = useCallback((startTime, robotId, eventId) => {
    if (!isComponentMounted.current || sharedState.processingUpdate) return;
    
    // 添加空值检查
    if (startTime === undefined || startTime === null) {
      startTime = 0;
      console.warn('MAP: Received invalid startTime in timeBlockClick, defaulting to 0');
    }
    
    if (!robotId) {
      console.warn('MAP: Received invalid robotId in timeBlockClick, ignoring click');
      return;
    }
    
    // 设置正确的视频URL
    const timePart = sharedState.timeframe === "11AM" ? "day" : "night";
    const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
    
    console.log(`Handling time block click: Time=${startTime}, Robot=${robotId}, Event=${eventId}, Video=${videoUrl}`);
    
    // 更新全局状态
    sharedState.updateVideoState({
      eventId,
      robotId,
      time: startTime,
      videoUrl
    }, 'map');
  }, [sharedState.processingUpdate, sharedState.timeframe, sharedState.updateVideoState]);
  
  // 监听视频播放状态变化
  useEffect(() => {
    const isPlaying = sharedState.isPlaying;
    setIsVideoPlaying(isPlaying);
    
    // 视频停止后，确保所有节流/防抖函数被清除
    if (!isPlaying) {
      if (debouncedUpdateRobotPositions) {
        debouncedUpdateRobotPositions.cancel();
      }
      if (throttledPositionUpdater.current) {
        throttledPositionUpdater.current.cancel();
      }
      
      // 视频停止后更新一次位置以确保地图显示正确
      if (updateRobotPositions && !isMapInteractionInProgress.current) {
        // 使用延迟确保UI响应
        setTimeout(() => {
          updateRobotPositions(sharedState.currentTime, false);
        }, 50);
      }
    }
  }, [sharedState.isPlaying, debouncedUpdateRobotPositions, updateRobotPositions, sharedState.currentTime]);
  
  // 监听currentTime变化 (仅在非视频播放状态下使用)
  useEffect(() => {
    if (!isComponentMounted.current || sharedState.processingUpdate || timelineData.length === 0) return;
    if (sharedState.currentTime <= 0) return;
    
    // 在视频播放状态或地图交互期间跳过
    if (isVideoPlaying || isMapInteractionInProgress.current) return;
    
    const currentTime = sharedState.currentTime;
    
    // 更新地图轨迹
    if (syncTimeChange) {
      syncTimeChange(currentTime);
    }
    
    // 仅在视频不播放时或强制更新时更新机器人位置
    if (updateRobotPositions) {
      updateRobotPositions(currentTime, false);
    }
  }, [sharedState.currentTime, sharedState.processingUpdate, syncTimeChange, timelineData.length, updateRobotPositions, isVideoPlaying]);

  // 切换视频展开状态
  const handleToggleExpand = useCallback(() => {
    setIsVideoExpanded(prev => !prev);
  }, []);

  // 设置视频可见性
  useEffect(() => {
    if (!isComponentMounted.current) return;
    const timer = setTimeout(() => {
      setIsVideoVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // 更新图标可见性
  const updateIconsVisibility = useCallback((routeIndex, visible) => {
    if (!isComponentMounted.current) return;
    
    setRoutes((prev) => {
      // 构建新状态
      const newRoutes = {
        ...prev,
        [routeIndex]: {
          ...prev[routeIndex],
          visible: visible,
        },
      };

      // 更新路由图标
      if (routeIconsManager.current) {
        const routeIcons = routeIconsManager.current.getIconsForRoute(routeIndex);
        routeIcons.forEach((icon) => {
          if (customMarkersRef.current?.isTypeVisible(icon.type)) {
            customMarkersRef.current?.updateRouteIcons([icon], visible);
          }
        });
      }

      // 更新图层可见性
      const polyline = polylinesRef.current[routeIndex];
      const backgroundPolyline = backgroundPolylinesRef.current[routeIndex];
      const marker = markersRef.current[routeIndex];
      
      if (mapInstance.current) {
        // 轨迹线处理
        if (polyline) {
          if (visible) {
            if (!mapInstance.current.hasLayer(polyline)) {
              mapInstance.current.addLayer(polyline);
            }
          } else {
            if (mapInstance.current.hasLayer(polyline)) {
              mapInstance.current.removeLayer(polyline);
            }
          }
        }
        
        // 背景轨迹线处理
        if (backgroundPolyline) {
          if (visible) {
            if (!mapInstance.current.hasLayer(backgroundPolyline)) {
              mapInstance.current.addLayer(backgroundPolyline);
            }
          } else {
            if (mapInstance.current.hasLayer(backgroundPolyline)) {
              mapInstance.current.removeLayer(backgroundPolyline);
            }
          }
        }
        
        // 标记点处理
        if (marker) {
          if (visible) {
            if (!mapInstance.current.hasLayer(marker)) {
              mapInstance.current.addLayer(marker);
            }
          } else {
            if (mapInstance.current.hasLayer(marker)) {
              mapInstance.current.removeLayer(marker);
            }
          }
        }
      }
      
      return newRoutes;
    });
  }, [customMarkersRef, mapInstance, polylinesRef, backgroundPolylinesRef, markersRef, setRoutes, routeIconsManager]);

  // 更新地图容器事件处理程序
  useEffect(() => {
    // 事件委托处理程序
    const handleMapContainerClick = (e) => {
      // 检查是否点击了map-event-link按钮
      if (e.target.classList.contains('map-event-link') || 
          e.target.closest('.map-event-link')) {
        
        // 获取最近的事件链接元素
        const linkElement = e.target.classList.contains('map-event-link') ? 
                           e.target : e.target.closest('.map-event-link');
        
        // 从数据属性中提取事件数据
        const robotId = parseInt(linkElement.getAttribute('data-robot-id'), 10);
        const eventId = parseInt(linkElement.getAttribute('data-event-id'), 10);
        const startTime = parseFloat(linkElement.getAttribute('data-start-time'));
        const frameStart = linkElement.getAttribute('data-frame-start');
        
        console.log(`Map popup clicked: Robot ${robotId}, Event ${eventId}, Time ${startTime}s, Frame ${frameStart}`);
        
        // 验证解析的值是否有效
        if (!isNaN(robotId) && !isNaN(eventId) && !isNaN(startTime)) {
          // 使用handleTimeBlockClick处理点击
          // 我们使用转换后的时间（秒）进行视频导航
          handleTimeBlockClick(startTime, robotId, eventId);
        } else {
          console.warn('Invalid data attributes in map event link:', { robotId, eventId, startTime });
        }
      }
    };
    
    const mapContainer = document.getElementById('map');
    if (mapContainer) {
      mapContainer.addEventListener('click', handleMapContainerClick);
    }
    
    // 清理函数
    return () => {
      if (mapContainer) {
        mapContainer.removeEventListener('click', handleMapContainerClick);
      }
    };
  }, [handleTimeBlockClick]);

  // 处理浏览器标签页可见性变化
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isTabVisible()) {
        // 页面不可见时暂停所有地图更新
        if (throttledPositionUpdater.current) {
          throttledPositionUpdater.current.cancel();
        }
        if (debouncedUpdateRobotPositions) {
          debouncedUpdateRobotPositions.cancel();
        }
      } else if (!isMapInteractionInProgress.current && !isVideoPlaying) {
        // 页面重新可见且不在播放/交互时，更新一次位置
        if (updateRobotPositions) {
          updateRobotPositions(sharedState.currentTime, false);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVideoPlaying, updateRobotPositions, sharedState.currentTime]);

  // 公开方法给父组件
  useImperativeHandle(ref, () => ({
    clearMap: () => {
      if (mapInstance.current) {
        clearMap();
      }
    },
    
    // 重建地图方法
    rebuildMap: () => {
      rebuildMap();
    },
    
    // 时间控制
    handleSliderChange: (value) => {
      if (!isComponentMounted.current || sharedState.processingUpdate) return;
      
      // 添加空值检查
      if (value === undefined || value === null) {
        console.warn('MAP: Received invalid time value in handleSliderChange, ignoring');
        return;
      }
      
      sharedState.updateVideoState({ time: value }, 'map');
      syncTimeChange(value);
    },
    
    // 视频选择
    handleVideoSelect,
    
    // 事件块点击
    handleTimeBlockClick,
    
    // 图标可见性
    updateIconsVisibility,
    
    // 对象跟踪
    handleTrackObject,
    
    // 获取当前时间
    getCurrentTime: () => {
      return videoRef.current ? videoRef.current.currentTime : sharedState.currentTime;
    },
    
    // 重置时间帧
    resetTimeframe: (newTimeframe) => {
      if (!isComponentMounted.current) return;
      
      console.log(`MAP: Resetting timeframe to ${newTimeframe}`);
      
      // 使用共享状态更新时间帧
      sharedState.updateVideoState({ timeframe: newTimeframe }, 'map');
      
      // 清理所有地图元素
      if (mapInstance.current) {
        // 清理轨迹线
        Object.values(polylinesRef.current).forEach(polyline => {
          if (polyline && mapInstance.current.hasLayer(polyline)) {
            mapInstance.current.removeLayer(polyline);
          }
        });
        
        // 清理背景轨迹线
        Object.values(backgroundPolylinesRef.current).forEach(polyline => {
          if (polyline && mapInstance.current.hasLayer(polyline)) {
            mapInstance.current.removeLayer(polyline);
          }
        });
        
        // 清理标记点
        Object.values(markersRef.current).forEach(marker => {
          if (marker && mapInstance.current.hasLayer(marker)) {
            mapInstance.current.removeLayer(marker);
          }
        });
      }
      
      // 清理自定义标记
      setCurrentVisibleIcons([]);
      if (customMarkersRef.current) {
        customMarkersRef.current.clear();
      }
      
      // 重置路由状态
      setRoutes({});
      
      // 重建地图
      rebuildMap();
    },
    
    // 禁用高亮
    disableHighlight: clearHighlight
  }));

  // 生成路线列表UI
  const getRouteColumns = useCallback(() => {
    const routeEntries = Object.entries(routes);
    const totalRoutes = routeEntries.length;
    const midpoint = Math.ceil(totalRoutes / 2);
    
    return {
      leftColumn: routeEntries.slice(0, midpoint),
      rightColumn: routeEntries.slice(midpoint)
    };
  }, [routes]);

  return (
    <div className="relative h-full">
      <div className="checkbox-container">
        <FilterControls 
          visibleTypes={visibleTypes} 
          toggleIconType={toggleIconType} 
        />
      </div>

      <div id="map" className="map-container" />
      
      {isVideoVisible && (
        <div
          style={{
            position: "absolute",
            left: isVideoExpanded ? "10%" : "16px",
            top: isVideoExpanded ? "10%" : "auto",
            bottom: isVideoExpanded ? "auto" : "16px",
            width: isVideoExpanded ? "80%" : "320px",
            height: isVideoExpanded ? "80%" : "180px",
            zIndex: 9999,
            backgroundColor: "black",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "row",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            transition: "all 0.3s ease-in-out",
            alignItems: "stretch",
          }}
        >
          {isVideoExpanded && (
            <div
              style={{
                width: "500px",
                flexShrink: 0,
                height: "100%",
                backgroundColor: "#555",
                borderRadius: "8px 0 0 8px",
                boxShadow: "2px 0 4px rgba(0, 0, 0, 0.3)",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                padding: "10px",
                overflowY: "auto",
              }}
            >
              <div style={{ flex: 1, overflow: "auto" }}>
                <VideoStory 
                  style={{ overflowY: "auto" }}
                  robotId={sharedState.activeRobotId || 1}
                  onEventClick={handleTimeBlockClick}
                  timeframe={sharedState.timeframe}
                  activeEventId={sharedState.activeEventId}
                  onTrackObject={handleTrackObject}
                />
              </div>
            </div>
          )}

          <VideoPlayer
            ref={videoRef}
            isExpanded={isVideoExpanded}
            onToggleExpand={handleToggleExpand}
            videoUrl={sharedState.videoUrl}
            hideExpandButton={false}
            onTimeUpdate={handleVideoTimeUpdate}
            onEndReached={() => {
              if (!sharedState.processingUpdate) {
                sharedState.updateVideoState({ isPlaying: false }, 'map');
              }
            }}
            style={{
              flex: 1,
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>
      )}
    </div>
  );
});

export default MAP;